/**
 * Owner: Masa Bayu
 * File: LoginPage.tsx
 * Date:Time: 2026-10-22 10:57
 * Description: §457 F5 — SATU borang identiti qxread melalui qxp-hub
 *   (`/v1/accounts/*`). Pilihan peranan **Reader / Publisher berada dalam borang
 *   utama** (bukan halaman berasingan) dan dihantar pada setiap jalan yang boleh
 *   MENCIPTA akaun: daftar kata laluan dan Continue with Google. Mesej ralat hub
 *   (BM) dipetakan ke teks UI di `hub-error-copy.ts`; yang tidak dikenali
 *   dipaparkan apa adanya.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  exchangeGoogleIdToken,
  loginWithPassword,
  logoutSession,
  registerWithPassword,
  type HubAccountRole,
} from '../auth/hub-session';
import { normalizeAuthEmail } from '../auth/auth-email';
import { safeAccountReturnTo } from '../auth/account-gate';
import { describeAuthError } from '../auth/hub-error-copy';
import { mountGoogleContinueButton } from '../auth/oauth-google';
import type { AuthSessionView } from '../auth/session-types';
import './login-page.css';

export interface LoginPageProps {
  readonly search: string;
  readonly signedInName: string | null;
  readonly onNavigate: (path: string) => void;
  readonly onSignedIn: (session: AuthSessionView) => void;
  readonly onSignOut: () => void;
}

type AuthMode = 'login' | 'register';

const ROLE_OPTIONS: ReadonlyArray<readonly [HubAccountRole, string, string]> = [
  ['reader', 'Reader', 'Buy and read eBooks'],
  ['publisher', 'Publisher', 'Publish and sell your books'],
];

const GOOGLE_FALLBACK = 'Google Sign-In failed. Try again.';

export function LoginPage({
  search,
  signedInName,
  onNavigate,
  onSignedIn,
  onSignOut,
}: LoginPageProps) {
  const [mode, setMode] = useState<AuthMode>('login');
  const [role, setRole] = useState<HubAccountRole>('reader');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const googleHost = useRef<HTMLDivElement>(null);
  // Google's callback is mounted once and fires later — read the role at click
  // time, otherwise a Publisher who picks the role after the button mounts
  // would silently be created as a Reader.
  const roleRef = useRef<HubAccountRole>('reader');
  const after = safeAccountReturnTo(new URLSearchParams(search).get('returnTo')) ?? '/account';
  const registering = mode === 'register';

  // Session restored on refresh while still on /login → continue to the intended screen.
  useEffect(() => {
    if (signedInName === null) return;
    onNavigate(after);
  }, [after, onNavigate, signedInName]);

  useEffect(() => {
    const parent = googleHost.current;
    if (!parent) return;
    let cancelled = false;
    let frame = 0;
    frame = window.requestAnimationFrame(() => {
      if (cancelled) return;
      void mountGoogleContinueButton(parent, (idToken) => {
        if (cancelled) return;
        setPending(true);
        setError(null);
        void exchangeGoogleIdToken({ idToken, role: roleRef.current })
          .then((session) => {
            if (cancelled) return;
            onSignedIn(session);
            onNavigate(after);
          })
          .catch((err: unknown) => {
            if (cancelled) return;
            setError(describeAuthError(err instanceof Error ? err.message : GOOGLE_FALLBACK));
          })
          .finally(() => {
            if (!cancelled) setPending(false);
          });
      }).catch((err: unknown) => {
        if (!cancelled) {
          setError(describeAuthError(err instanceof Error ? err.message : GOOGLE_FALLBACK));
        }
      });
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    };
  }, [after, onNavigate, onSignedIn]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const session = registering
        ? await registerWithPassword({
            email: normalizeAuthEmail(email),
            password,
            displayName: displayName.trim(),
            role,
          })
        : await loginWithPassword({
            email: normalizeAuthEmail(email),
            password,
          });
      setPassword('');
      onSignedIn(session);
      onNavigate(after);
    } catch (err) {
      setError(describeAuthError(err instanceof Error ? err.message : 'Sign in failed'));
    } finally {
      setPending(false);
    }
  }

  function switchMode(next: AuthMode): void {
    setMode(next);
    setError(null);
  }

  function chooseRole(next: HubAccountRole): void {
    setRole(next);
    roleRef.current = next;
  }

  return (
    <section className="login-page" aria-labelledby="login-title">
      <div className="login-page__card">
        <h2 id="login-title">{registering ? 'Create your QX account' : 'Welcome back'}</h2>
        <p className="login-page__lede">
          {registering ? 'Sign up to buy eBooks and build your library' : 'Sign in to continue'}
        </p>
        {signedInName !== null ? (
          <p className="login-page__session">
            Signed in as <strong>{signedInName}</strong>.
            <button
              type="button"
              onClick={() => {
                void logoutSession().finally(onSignOut);
              }}
            >
              Sign out
            </button>
          </p>
        ) : null}
        <div ref={googleHost} className="login-page__google-host" />
        <p className="login-page__or">or</p>
        <form className="login-page__form" onSubmit={(event) => void onSubmit(event)}>
          <fieldset className="login-page__roles">
            <legend>Account type</legend>
            <div className="login-page__role-options">
              {ROLE_OPTIONS.map(([value, label, hint]) => (
                <label
                  key={value}
                  className={`login-page__role${role === value ? ' login-page__role--on' : ''}`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={value}
                    checked={role === value}
                    disabled={pending}
                    onChange={() => {
                      chooseRole(value);
                    }}
                  />
                  <span className="login-page__role-text">
                    <span className="login-page__role-label">{label}</span>
                    <span className="login-page__role-hint">{hint}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="login-page__role-note">
              {registering
                ? 'Sets the role of the account we create for you.'
                : 'Used if this sign-in creates a new QX account. Existing accounts keep their role.'}
            </p>
          </fieldset>
          {registering ? (
            <label>
              Name
              <input
                name="displayName"
                type="text"
                autoComplete="name"
                placeholder="Your name"
                value={displayName}
                required
                disabled={pending}
                onChange={(event) => {
                  setDisplayName(event.target.value);
                }}
              />
            </label>
          ) : null}
          <label>
            Email
            <input
              name="email"
              type="email"
              autoComplete="username"
              placeholder="you@example.com"
              value={email}
              required
              disabled={pending}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </label>
          <label>
            Password
            <span className="login-page__password">
              <input
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete={registering ? 'new-password' : 'current-password'}
                placeholder={registering ? 'At least 8 characters' : 'Your password'}
                value={password}
                required
                minLength={registering ? 8 : undefined}
                disabled={pending}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
              <button
                type="button"
                className="login-page__reveal"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                disabled={pending}
                onClick={() => {
                  setShowPassword((current) => !current);
                }}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </span>
          </label>
          {error !== null ? (
            <p className="login-page__error" role="alert">
              {error}
            </p>
          ) : null}
          <button type="submit" className="login-page__submit" disabled={pending}>
            {pending
              ? registering
                ? 'Creating account…'
                : 'Signing in…'
              : registering
                ? 'Create Account'
                : 'Sign in'}
          </button>
        </form>
        <button
          type="button"
          className="login-page__switch"
          disabled={pending}
          onClick={() => {
            switchMode(registering ? 'login' : 'register');
          }}
        >
          {registering ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
        </button>
      </div>
    </section>
  );
}
